import { ArrowUpRight, ExternalLink } from "lucide-react";
import { useCountUp } from "@/hooks/useCountUp";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { ResearchDrawer } from "@/components/ResearchDrawer";
import { getEffectBlock, type EffectStat } from "@/lib/ingredientEffects";
import { getFormulaKeyForHandle } from "@/lib/ingredientBenefits";

function StatNumber({ value }: { value: number }) {
  const { ref, seen } = useInViewOnce<HTMLSpanElement>({ threshold: 0.4 });
  const display = useCountUp(seen ? value : 0, 1200);
  return (
    <span ref={ref} className="tabular-nums" style={{ fontVariantNumeric: "tabular-nums" }}>
      {Math.round(display).toLocaleString("en-IN")}
    </span>
  );
}

function StatCardBody({ stat, clickable }: { stat: EffectStat; clickable: boolean }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-base font-semibold leading-snug text-brand-deep">
          {stat.label}
        </p>
        {clickable && (
          <ArrowUpRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        )}
      </div>
      {clickable && (
        <span className="mt-1 inline-block border-b border-dashed border-brand/50 text-xs font-semibold text-brand">
          Read more
        </span>
      )}

      <p className="mt-6 font-display text-4xl font-bold leading-none text-brand md:text-5xl">
        {stat.prefix}
        <StatNumber value={stat.value} />
        {stat.suffix}
      </p>
      <p className="mt-2 text-sm text-foreground/75">{stat.qualifier}</p>
      <p className="mt-3 text-xs font-medium text-brand-deep">{stat.active}</p>
      <p className="mt-1 text-xs text-muted-foreground">{stat.design}</p>
      {stat.citationUrl && stat.citationLabel && (
        <span className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          {stat.citationLabel}
        </span>
      )}
    </>
  );
}

export function StudiedEffects({ handle }: { handle: string }) {
  const key = getFormulaKeyForHandle(handle);
  if (!key) return null;
  const block = getEffectBlock(key);

  return (
    <section className="mt-10 md:mt-16" aria-label="Published research on these actives">
      <div className="rounded-[2rem] border border-border bg-brand-soft/40 p-5 md:p-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand">
            {block.eyebrow}
          </p>
          <h2 className="mt-2 font-display text-3xl font-bold text-brand-deep md:text-4xl">
            {block.title}
          </h2>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">{block.intro}</p>
        </div>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {block.stats.map((stat) => (
            <li key={`${stat.label}-${stat.active}`}>
              {stat.slug ? (
                <ResearchDrawer defaultSlug={stat.slug}>
                  <button
                    type="button"
                    className="group flex h-full w-full flex-col rounded-3xl border border-border bg-card p-5 text-left transition hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md md:p-6"
                  >
                    <StatCardBody stat={stat} clickable />
                  </button>
                </ResearchDrawer>
              ) : (
                <div className="flex h-full flex-col rounded-3xl border border-border bg-card/70 p-5 md:p-6">
                  <StatCardBody stat={stat} clickable={false} />
                </div>
              )}
            </li>
          ))}
        </ul>

        <p className="mt-6 text-center text-[11px] leading-relaxed text-muted-foreground">
          Figures describe published research on the individual actives and their delivery forms,
          not finished-product outcomes. Food supplement, not a medicine. These statements have not
          been evaluated by the FSSAI or FDA.
        </p>
      </div>
    </section>
  );
}
